/**
 * User Detail Permissions Hook
 *
 * Type-safe wrapper for user detail dialog permissions.
 * Converts async permission checks to synchronous boolean state.
 *
 * This hook provides granular permissions for user management dialogs,
 * including field-level edit permissions based on roles and context.
 *
 * Usage:
 * ```typescript
 * const { permissions, isLoading } = useUserDetailPermissions(
 *   userId,
 *   userRole,
 *   'edit'
 * );
 *
 * if (permissions.canEditUserRole) {
 *   // Show role field as editable
 * }
 * ```
 */

import { useEntityPermissions } from './useEntityPermissions';
import { useUserContext } from '@/hooks/use-user-context';
import { useMemo } from 'react';

/**
 * User detail management permissions interface
 */
export interface UserDetailPermissions extends Record<string, boolean> {
  canViewUser: boolean;
  canEditProfileFields: boolean;
  canEditAssignmentPermissions: boolean;
  canEditEmailNotifications: boolean;
  canEditUserStatus: boolean;
  canEditUserRole: boolean;
}

/**
 * Result of useUserDetailPermissions hook
 */
export interface UseUserDetailPermissionsResult {
  permissions: UserDetailPermissions;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for user detail dialog permissions.
 *
 * Implements the permission logic for user management dialogs:
 * - System Admins: Can edit all fields for all users
 * - Company Admins: Can edit users in their company with restrictions
 * - Regular Users: Can only edit their own email notification preferences (not permission levels)
 *
 * @param userId - The user ID being edited (undefined for create mode)
 * @param targetUserRole - The role of the user being edited
 * @param mode - Dialog mode ('create' or 'edit')
 * @returns Object containing sync boolean permissions, loading state, and error
 *
 * @example
 * ```typescript
 * // In user dialog
 * const UserEditDialog = ({ userId, userRole }) => {
 *   const { permissions, isLoading } = useUserDetailPermissions(
 *     userId,
 *     userRole,
 *     'edit'
 *   );
 *
 *   return (
 *     <Form>
 *       {permissions.canEditUserRole && <RoleField />}
 *       {permissions.canEditUserStatus && <StatusField />}
 *       {permissions.canEditAssignmentPermissions && <PermissionLevelDropdowns />}
 *       {permissions.canEditEmailNotifications && <EmailNotificationToggles />}
 *     </Form>
 *   );
 * };
 * ```
 */
export function useUserDetailPermissions(
  userId?: string,
  targetUserRole?: string,
  mode?: 'create' | 'edit'
): UseUserDetailPermissionsResult {
  const userContext = useUserContext();
  const isEdit = mode === 'edit';

  // Memoize computed permission functions to prevent unnecessary re-renders
  const permissionFunctions = useMemo(() => {
    /**
     * Can view user
     * - All authenticated users can view users based on RLS policies
     */
    const canViewUserCheck = async (): Promise<boolean> => {
      // Basic view permission - RLS enforces actual data access
      return !!userContext;
    };

    /**
     * Can edit assignment permissions (project permission levels)
     * - System Admins: Always
     * - Company Admins: Always for users in their company
     * - Regular Users: Never (cannot change their own permission levels)
     */
    const canEditAssignmentPermissionsCheck = async (): Promise<boolean> => {
      // System Admins can edit everything
      if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
        return true;
      }

      // Company Admins can edit users in their company
      if (userContext?.app_role === 'COMPANY_ADMIN') {
        return true;
      }

      // Regular users cannot edit assignment permissions (even their own)
      return false;
    };

    /**
     * Can edit email notification preferences
     * - System Admins: Always
     * - Company Admins: Always for users in their company
     * - Regular Users: Only their own email notification preferences
     */
    const canEditEmailNotificationsCheck = async (): Promise<boolean> => {
      // System Admins can edit everything
      if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
        return true;
      }

      // Company Admins can edit users in their company
      if (userContext?.app_role === 'COMPANY_ADMIN') {
        return true;
      }

      // Regular users can edit their own email notification preferences
      if (isEdit && userId && userContext && userId === userContext.id) {
        return true;
      }

      return false;
    };

    /**
     * Can edit profile fields (role, company, status)
     * - System Admins: Always
     * - Company Admins: Yes
     * - Regular Users: No
     */
    const canEditProfileFieldsCheck = async (): Promise<boolean> => {
      // System Admins can edit everything
      if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
        return true;
      }

      // Company Admins can edit certain fields
      if (userContext?.app_role === 'COMPANY_ADMIN') {
        return true;
      }

      return false;
    };

    /**
     * Can edit user status (ACTIVE/INACTIVE)
     * - System Admins: Always
     * - Company Admins: Only for regular users (not for themselves or other admins)
     * - Regular Users: No
     */
    const canEditUserStatusCheck = async (): Promise<boolean> => {
      // System Admins can see/edit status on everyone
      if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
        return true;
      }

      // Company Admins should only see status on regular users
      if (userContext?.app_role === 'COMPANY_ADMIN') {
        if (isEdit) {
          // Don't show status for Company Admins (themselves or others)
          return targetUserRole === 'USER';
        } else {
          // In create mode, show status field (they can create users with status)
          return true;
        }
      }

      return false;
    };

    /**
     * Can edit user role (USER, COMPANY_ADMIN, ADMIN)
     * - System Admins: Always
     * - Company Admins: Only for regular users (not for themselves or other admins)
     * - Regular Users: No
     */
    const canEditUserRoleCheck = async (): Promise<boolean> => {
      // System Admins can edit role on everyone
      if (userContext?.is_system_admin || userContext?.app_role === 'ADMIN') {
        return true;
      }

      // Company Admins should only be able to edit role for regular users
      if (userContext?.app_role === 'COMPANY_ADMIN') {
        if (isEdit) {
          // Only allow editing role for regular users
          return targetUserRole === 'USER';
        } else {
          // In create mode, allow setting role for new users
          return true;
        }
      }

      return false;
    };

    return {
      canViewUserCheck,
      canEditAssignmentPermissionsCheck,
      canEditEmailNotificationsCheck,
      canEditProfileFieldsCheck,
      canEditUserStatusCheck,
      canEditUserRoleCheck,
    };
  }, [userContext, isEdit, userId, targetUserRole]);

  return useEntityPermissions<UserDetailPermissions>(
    {
      canViewUser: {
        fn: permissionFunctions.canViewUserCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
      canEditProfileFields: {
        fn: permissionFunctions.canEditProfileFieldsCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
      canEditAssignmentPermissions: {
        fn: permissionFunctions.canEditAssignmentPermissionsCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
      canEditEmailNotifications: {
        fn: permissionFunctions.canEditEmailNotificationsCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
      canEditUserStatus: {
        fn: permissionFunctions.canEditUserStatusCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
      canEditUserRole: {
        fn: permissionFunctions.canEditUserRoleCheck as (..._args: unknown[]) => Promise<boolean>,
        args: [],
      },
    },
    [userId, targetUserRole, mode, userContext?.id, userContext?.app_role, userContext?.is_system_admin]
  );
}
